import { Box, Paper, Tooltip, useColorScheme, useTheme } from "@mui/material";
import {
  Background,
  Controls,
  Edge,
  Handle,
  MiniMap,
  Node,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useOnSelectionChange,
  useOnViewportChange,
  useReactFlow,
  Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import ELK from "elkjs/lib/elk.bundled";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

// Type definitions
export interface FlowChartNode {
  id: string;
  label: string;
  type?: string;
  data?: any;
}

export interface FlowChartEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  data?: any;
  type: "dashed" | "solid";
}

export interface FlowChartData {
  nodes: FlowChartNode[];
  edges: FlowChartEdge[];
}

interface FlowChartProps {
  data: FlowChartData;
  width?: string | number;
  height?: string | number;
  direction?: "DOWN" | "UP" | "RIGHT" | "LEFT";
  nodeSpacing?: number;
  layerSpacing?: number;
  className?: string;
  minimap?: boolean;
  controls?: boolean;
  selectedNodeId?: string;
  defaultZoom?: number;
  onSelection?: (node: Node) => void;
}

// Smart layout function that respects the logical flow
// ELK.js layout for advanced graph positioning with async support
const useELKLayout = (
  nodes: Node[],
  edges: Edge[],
  direction: "DOWN" | "UP" | "RIGHT" | "LEFT" = "DOWN"
) => {
  const [layoutedNodes, setLayoutedNodes] = React.useState<Node[]>([]);
  const [isLayouting, setIsLayouting] = React.useState(false);

  React.useEffect(() => {
    if (nodes.length === 0) return;

    const runLayout = async () => {
      setIsLayouting(true);

      const elk = new ELK();

      // Convert ReactFlow nodes/edges to ELK format
      const elkNodes = nodes.map((node) => ({
        id: node.id,
        width: 200,
        height: 100,
      }));

      const elkEdges = edges.map((edge) => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
      }));

      const elkGraph = {
        id: "root",
        layoutOptions: {
          "elk.algorithm": "layered",
          "elk.direction": direction,
          "elk.spacing.nodeNode": "150", // Aumentato da 80 a 120
          "elk.layered.spacing.nodeNodeBetweenLayers": "80", // Aumentato da 120 a 150
          "elk.spacing.edgeNode": "40", // Spazio minimo tra edge e nodi
          "elk.spacing.edgeEdge": "20", // Spazio minimo tra edge paralleli
          "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
        },
        children: elkNodes,
        edges: elkEdges,
      };

      try {
        const layoutedGraph = await elk.layout(elkGraph);
        const newNodes = nodes.map((node) => {
          const elkNode = layoutedGraph.children?.find(
            (child) => child.id === node.id
          );
          return {
            ...node,
            position: {
              x: elkNode?.x || 0,
              y: elkNode?.y || 0,
            },
          };
        });

        setLayoutedNodes(newNodes);
        // console.log("ELK layout completed successfully");
      } catch (error) {
        // console.error("ELK layout failed:", error);
        // Fallback to simple grid layout
        const fallbackNodes = nodes.map((node, index) => ({
          ...node,
          position: {
            x: 100 + (index % 4) * 250,
            y: 100 + Math.floor(index / 4) * 150,
          },
        }));
        setLayoutedNodes(fallbackNodes);
      }

      setIsLayouting(false);
    };

    runLayout();
  }, [nodes, edges, direction]);

  return useMemo(
    () => ({
      nodes: layoutedNodes,
      edges,
      isLayouting,
    }),
    [layoutedNodes, edges, isLayouting]
  );
};

// Custom node component with better styling
const CustomNode = ({ data, selected }: { data: any; selected?: boolean }) => {
  const theme = useTheme();

  // Use both ReactFlow's selected prop and our custom data.selected
  const isSelected = selected || data.selected;

  // Debug: log when node is selected
  if (isSelected) {
    console.log(
      "Selected node:",
      data.label,
      "isSelected:",
      isSelected,
      "reactflow selected:",
      selected,
      "data.selected:",
      data.selected
    );
  }

  // Different styles based on node type
  const getNodeStyle = () => {
    const baseStyle = {
      padding: 1.5,
      minWidth: 140,
      minHeight: 50,
      textAlign: "center" as const,
      borderRadius: 2,
      cursor: "pointer",
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "center",
      alignItems: "center",
    };

    switch (
      data.originalData === null
        ? data.subtitle?.includes("Avvio")
          ? "start"
          : "end"
        : "enigma"
    ) {
      case "start":
        return {
          ...baseStyle,
          backgroundColor: theme.palette.success.light,
          border: `2px solid ${theme.palette.success.main}`,
          color: theme.palette.success.contrastText,
        };
      case "end":
        return {
          ...baseStyle,
          backgroundColor: theme.palette.primary.light,
          border: `2px solid ${theme.palette.primary.main}`,
          color: theme.palette.primary.contrastText,
        };
      default:
        if (isSelected) {
          return {
            ...baseStyle,
            backgroundColor: theme.palette.warning.main,
            border: `3px solid ${theme.palette.warning.dark}`,
            color: theme.palette.warning.contrastText,
            boxShadow: `0 0 10px ${theme.palette.warning.main}`,
            transform: "scale(1.05)", // Slightly enlarge selected node
          };
        } else {
          return {
            ...baseStyle,
            backgroundColor: theme.palette.background.paper,
            border: `2px solid ${theme.palette.grey[400]}`,
            color: theme.palette.text.primary,
          };
        }
    }
  };

  return (
    <Tooltip
      title={
        data.tooltip ? (
          <div dangerouslySetInnerHTML={{ __html: data.tooltip }}></div>
        ) : (
          "Nessuna descrizione disponibile"
        )
      }
    >
      <Paper elevation={3} sx={getNodeStyle()}>
        <div
          style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "4px" }}
        >
          {data.label}
        </div>
        {data.subtitle && (
          <div style={{ fontSize: "11px", opacity: 0.8 }}>{data.subtitle}</div>
        )}
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: theme.palette.primary.main }}
        />
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: theme.palette.primary.main }}
        />
      </Paper>
    </Tooltip>
  );
};

// Node types
const nodeTypes = {
  custom: CustomNode,
};

// Internal component that uses ReactFlow hooks
const FlowChartInternal: React.FC<FlowChartProps> = ({
  data,
  width = "100%",
  height = 400,
  direction = "RIGHT",
  nodeSpacing = 50,
  layerSpacing = 100,
  className,
  minimap = false,
  controls = true,
  selectedNodeId, // IGNORE
  defaultZoom = 1, // IGNORE
  onSelection, // IGNORE
}) => {
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const { fitView, setCenter, updateNodeData } = useReactFlow();

  // Convert input data to ReactFlow format
  const initialNodes: Node[] = useMemo(
    () =>
      data.nodes.map((node) => ({
        id: node.id,
        type: "custom",
        position: { x: 0, y: 0 }, // Will be set by layout
        data: {
          label: node.label,
          tooltip: node.data?.tooltip,
          subtitle: node.data?.subtitle,
          selected: node.id === selectedNodeId,
          ...node.data,
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      })),
    [data.nodes]
  );

  const initialEdges: Edge[] = useMemo(
    () =>
      data.edges.reduce((acc, edge) => {
        if (acc.some((e) => e.id === edge.id)) return acc; // Avoid duplicate edges
        acc.push({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.label,
          type: "default",
          animated: false, // Remove animation to reduce visual noise
          style: {
            stroke: theme.palette.primary.main,
            strokeWidth: 2,
            strokeDasharray: edge.type == "dashed" ? "5,5" : undefined,
          },
          labelStyle: {
            fontSize: "10px",
            fontWeight: "normal",
            fill: theme.palette.text.secondary,
          },
          labelBgStyle: {
            fill: theme.palette.background.paper,
            fillOpacity: 0.8,
          },
        });
        return acc;
      }, [] as Edge[]),
    [data.edges, theme]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [zoom, setZoom] = useState<number>(defaultZoom);

  // Use simple layout instead of complex ELK layout
  const { nodes: layoutedNodes, edges: layoutedEdges } = useELKLayout(
    initialNodes,
    initialEdges,
    direction
  );

  const centerNode = (
    node: Node,
    yGutter: number = 0,
    xGutter: number = 50
  ) => {
    setCenter(node.position.x + xGutter, node.position.y + yGutter, {
      zoom: zoom,
    });
  };

  useEffect(() => {
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);

    setTimeout(() => {
      const selectedNode = layoutedNodes.find((n) => n.data.selected);
      if (selectedNode) {
        centerNode(selectedNode, 0);
      } else {
        const startingNode = layoutedNodes.find((n) => n.id === "start");
        if (startingNode) {
          centerNode(startingNode, 200);
        }
      }
    }, 100);
  }, [layoutedNodes, layoutedEdges, setNodes, setEdges, fitView]);

  const onSelectionChange = useCallback(
    ({ nodes }: { nodes: Node[]; edges: Edge[] }) => {
      onSelection?.(nodes[0]);
    },
    [onSelection]
  );

  const onZoomChange = useCallback(({ zoom }: Viewport) => {
    setZoom(zoom);
  }, []);

  useOnViewportChange({
    onChange: onZoomChange,
  });

  useOnSelectionChange({
    onChange: onSelectionChange,
  });

  useEffect(() => {
    console.log(
      "FlowChart useEffect: selectedNodeId changed to:",
      selectedNodeId
    );

    // Use ReactFlow's native selection mechanism
    if (selectedNodeId) {
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: n.id === selectedNodeId,
          data: { ...n.data, selected: n.id === selectedNodeId },
        }))
      );
    } else {
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          selected: false,
          data: { ...n.data, selected: false },
        }))
      );
    }
  }, [selectedNodeId]);

  return (
    <Box
      className={className}
      sx={{
        width,
        height,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        nodesDraggable={false} // Disable dragging for cleaner look
        nodesConnectable={false} // Disable connecting for cleaner look
        elementsSelectable={true} // Allow selection for better UX
        fitViewOptions={{
          maxZoom: defaultZoom,
        }}
        colorMode={colorScheme.mode}
      >
        <Background id="flow-bg"  />
        <Controls position="top-left" />
        {minimap && (
          <>
            <MiniMap
              position="bottom-right"
              style={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
              nodeColor={theme.palette.primary.main}
            />
            <Panel position="top-right">
              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  padding: 1,
                  borderRadius: 1,
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                }}
              >
                {nodes.length} enigmi, {edges.length} connessioni
              </Box>
            </Panel>
          </>
        )}
      </ReactFlow>
    </Box>
  );
};

// Main component wrapped with ReactFlowProvider
export const FlowChart: React.FC<FlowChartProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowChartInternal {...props} />
    </ReactFlowProvider>
  );
};

export default FlowChart;
